import React, { useState, useEffect } from 'react';
import FixedStartTime from './FixedStartTime';
import IntervalDuration from './IntervalDuration';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	Box,
	Typography,
	TextField,
	FormHelperText,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio,
} from '@mui/material';
import { StartTimeSlots, FixedTimesObject, StartTimeInterval, StartTimeFixed } from 'common/types';
import { isFixed, isInterval } from 'common/types/typeGuards';
import { useTranslation } from 'services/localization/useTranslation';

interface Props {
	value: StartTimeSlots;
	onChange: (value: StartTimeSlots) => void;
	disabled?: boolean;
}

const StartTimeSlotsView = (props: Props) => {
	const [limitError, setLimitError] = useState(false);

	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException)
		.openingHours;
	const { value, onChange } = props;
	const classes = useStyles();
	const { orderLimit } = value;
	const { t } = useTranslation();

	useEffect(() => {
		if (limitError && orderLimit === null) {
			setLimitError(false);
		}
	}, [orderLimit, limitError]);

	const getDefaultFixedTimeWithDayType = (): FixedTimesObject => {
		return {
			dayType: openingHours?.defaultTimes[0]?.dayType ?? 'allDays',
			time: openingHours?.defaultTimes[0]?.openTime ?? '09:00',
		};
	};

	const handleFixedTimesChange = (changedValue: StartTimeSlots) => {
		onChange(changedValue);
	};

	const handleIntervalDurationChange = (changedValue: StartTimeSlots) => {
		onChange(changedValue);
	};

	const handleOrderLimitChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const parsedValue = parseInt(e.target.value);
		!isNaN(parsedValue) && setLimitError(!(parsedValue > 0));
		onChange({
			...value,
			orderLimit: parsedValue,
		});
	};

	const handleOrderLimitError = () => {
		if (orderLimit != null && (!orderLimit || orderLimit <= 0)) {
			onChange({
				...value,
				orderLimit: null,
			});
			setLimitError(false);
		}
	};

	const handleUseOrderLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const changedValue = e.target.value as 'limit' | 'noLimit';
		onChange({
			...value,
			orderLimit: changedValue === 'limit' ? 1 : null,
		});
	};

	const handleStartTimeSlotsTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const changedValue = e.target.value as 'interval' | 'fixed';
		if (changedValue === 'fixed') {
			const obj: StartTimeFixed = {
				...value,
				type: 'fixed',
				fixedTimes: value.fixedTimes ?? [getDefaultFixedTimeWithDayType()],
				outsideOpeningHours: value.outsideOpeningHours ?? false,
			};
			onChange(obj);
		} else {
			const obj: StartTimeInterval = {
				...value,
				type: 'interval',
				intervalMinutes: value.intervalMinutes ?? 60,
			};
			onChange(obj);
		}
	};

	const renderLimitOrdersPerStartTime = () => (
		<>
			<Typography variant="body1" className={classes.spacingTop}>
				{t('products.limitOrdersPerStartTime', 'Limit orders per start time')}
			</Typography>
			<Typography variant="body2" display="block" color="textSecondary" gutterBottom>
				{t(
					'products.limitOrdersInfo',
					'By default, only product availability limits available start times. You can also limit how many orders with this product you take in per start time.',
				)}
			</Typography>
			<FormControl component="fieldset">
				<RadioGroup
					aria-label="orderLimit"
					name="orderLimit"
					value={orderLimit != null ? 'limit' : 'noLimit'}
					onChange={handleUseOrderLimitChange}
				>
					<FormControlLabel
						value="noLimit"
						control={<Radio color="primary" />}
						label={t('products.noLimit', 'No order limit')}
						disabled={props.disabled}
					/>
					<FormControlLabel
						value="limit"
						control={<Radio color="primary" />}
						disabled={props.disabled}
						label={
							<Box className={classes.radioLabel}>
								<Typography style={{ marginRight: 8 }}>{t('products.maximum', 'Max')}</Typography>
								<TextField
									name="limit"
									variant="standard"
									type="number"
									margin="normal"
									className={classes.textField}
									value={orderLimit != null && !isNaN(orderLimit) ? orderLimit : ''}
									onChange={handleOrderLimitChange}
									onBlur={handleOrderLimitError}
									disabled={props.disabled || orderLimit == null}
									InputLabelProps={{
										shrink: true,
									}}
									inputProps={{
										min: 1,
									}}
								/>
								<Typography>{t('products.perStartTime', 'per start time')}</Typography>
							</Box>
						}
					/>
				</RadioGroup>
				{orderLimit != null && limitError && (
					<FormHelperText error>
						{t(
							'products.limitError',
							`Limit can't be zero or negative. In this case, setting will default to no limit`,
						)}
						.
					</FormHelperText>
				)}
			</FormControl>
		</>
	);

	return (
		<Box>
			<FormControl component="fieldset" fullWidth>
				<RadioGroup
					aria-label="StartTimeType"
					name="StartTimeType"
					value={value.type}
					onChange={handleStartTimeSlotsTypeChange}
				>
					<FormControlLabel
						value="interval"
						control={<Radio color="primary" className={classes.alignRadioTop} />}
						label={t(
							'products.startTimeDescription',
							'Specify the interval between start time slots',
						)}
						className={classes.alignRadio}
						disabled={props.disabled}
					/>
					{isInterval(value) && (
						<IntervalDuration
							value={value}
							onChange={handleIntervalDurationChange}
							disabled={props.disabled}
						/>
					)}
					<FormControlLabel
						value="fixed"
						control={<Radio color="primary" className={classes.alignRadioTop} />}
						label={t('products.useFixedStartTimes', 'Use fixed start times')}
						className={classes.alignRadio}
						classes={{
							label: classes.fullFlex,
						}}
						disabled={props.disabled}
					/>
					{isFixed(value) && (
						<FixedStartTime
							value={value}
							onChange={handleFixedTimesChange}
							disabled={props.disabled}
						/>
					)}
				</RadioGroup>
			</FormControl>
			{renderLimitOrdersPerStartTime()}
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		spacingTop: {
			marginTop: theme.spacing(4),
		},
		dropdown: {
			width: 87,
			marginRight: theme.spacing(2),
		},
		intervalSelection: {
			display: 'flex',
		},
		radioLabel: {
			display: 'flex',
			alignItems: 'baseline',
		},
		textField: {
			width: 50,
			margin: theme.spacing(0, 1, 0, 0),
		},
		alignRadio: {
			alignItems: 'baseline',
		},
		alignRadioTop: {
			top: 7,
		},
		addRowBtn: {
			marginTop: theme.spacing(3),
		},
		fullFlex: {
			flex: 1,
		},
	}),
);

export default StartTimeSlotsView;
